import React, { useState, useEffect, useRef, useMemo } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
	Box,
	Typography,
	InputBase,
	IconButton,
	Chip,
	Dialog,
	Slide,
	Fade,
	Button,
	CircularProgress,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import CloseIcon from "@mui/icons-material/Close";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import MapOutlinedIcon from "@mui/icons-material/MapOutlined";
import FlightTakeoffIcon from "@mui/icons-material/FlightTakeoff";
import FolderOutlinedIcon from "@mui/icons-material/FolderOutlined";
import LightbulbOutlinedIcon from "@mui/icons-material/LightbulbOutlined";
import SendIcon from "@mui/icons-material/Send";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import KeyboardCommandKeyIcon from "@mui/icons-material/KeyboardCommandKey";
import { useFirebase } from "../../context/ApiContext";

/* ─── Destinos populares (las que más busca la gente) ─── */
const POPULAR_DESTINATIONS = [
	"Tokio",
	"Nueva York",
	"París",
	"Kioto",
	"Londres",
	"Seúl",
	"San Francisco",
	"Bangkok",
	"Bali",
	"Osaka",
];

/* ─── Tags rápidos para filtrar ─── */
const QUICK_TAGS = [
	{ label: "Asia" },
	{ label: "Europa" },
	{ label: "América" },
	{ label: "Plantillas" },
	{ label: "Travel Kit" },
];

/* ─── Transition ─── */
const SlideUp = React.forwardRef(function Transition(props, ref) {
	return <Slide direction="up" ref={ref} {...props} />;
});

const SearchOverlay = ({
	open,
	onClose,
	prebuiltGuides = [],
	myGuides = [],
	imageMap = {},
	onComingSoonGuide = null,
}) => {
	const navigate = useNavigate();
	const location = useLocation();
	const { user, submitGuideProposal } = useFirebase();
	const inputRef = useRef(null);
	const [query, setQuery] = useState("");
	const [showSuggestForm, setShowSuggestForm] = useState(false);
	const [suggestion, setSuggestion] = useState("");
	const [suggestionSent, setSuggestionSent] = useState(false);
	const [suggestionError, setSuggestionError] = useState(null);
	const [suggestionLoading, setSuggestionLoading] = useState(false);
	const [activeTag, setActiveTag] = useState(null);

	// Focus input when dialog opens
	useEffect(() => {
		if (open) {
			setQuery("");
			setShowSuggestForm(false);
			setSuggestionSent(false);
			setSuggestion("");
			setSuggestionError(null);
			setActiveTag(null);
			// Small delay to ensure dialog is rendered
			setTimeout(() => {
				inputRef.current?.focus();
			}, 100);
		}
	}, [open]);

	// ─── Filter logic ───
	const normalizeText = (text) =>
		(text || "")
			.toLowerCase()
			.normalize("NFD")
			.replace(/[\u0300-\u036f]/g, "");

	const filteredPrebuilt = useMemo(() => {
		let guides = prebuiltGuides;

		// Filter by active tag
		if (activeTag) {
			const tagNorm = normalizeText(activeTag);
			guides = guides.filter((g) => {
				const tags = (g.tags || []).map(normalizeText);
				const cat = normalizeText(g.category);
				const name = normalizeText(g.name);
				if (tagNorm === "plantillas") return cat === "utility";
				if (tagNorm === "travel kit") return cat === "travelkit";
				return tags.some((t) => t.includes(tagNorm)) || name.includes(tagNorm);
			});
		}

		// Filter by search query
		if (query.trim()) {
			const q = normalizeText(query);
			guides = guides.filter(
				(g) =>
					normalizeText(g.name).includes(q) ||
					normalizeText(g.description).includes(q) ||
					(g.tags || []).some((t) => normalizeText(t).includes(q))
			);
		}

		return guides;
	}, [prebuiltGuides, query, activeTag]);

	const filteredMyGuides = useMemo(() => {
		if (!query.trim()) return myGuides.slice(0, 4);
		const q = normalizeText(query);
		return myGuides.filter((g) => normalizeText(g.name).includes(q));
	}, [myGuides, query]);

	const hasResults = filteredPrebuilt.length > 0 || filteredMyGuides.length > 0;
	const isSearching = query.trim().length > 0 || activeTag;

	// ─── Handlers ───
	const handleGuideClick = (guide) => {
		onClose();
		if (guide.isMyGuide && user) {
			const userId = user.uid || user.id;
			navigate(`/usuarios/${userId}/guias/${guide.id}`);
		} else {
			navigate(`/explorar`);
		}
	};

	const handlePrebuiltClick = (guide) => {
		if (guide.availableForPurchase === false && onComingSoonGuide) {
			onComingSoonGuide(guide);
			onClose();
			return;
		}
		onClose();
		navigate(`/preview/prebuilt/${guide.id}`, {
			state: { from: `${location.pathname}${location.search}` },
		});
	};

	const handleSuggest = async () => {
		if (!suggestion.trim()) return;
		setSuggestionError(null);
		setSuggestionLoading(true);
		try {
			await submitGuideProposal(
				suggestion.trim(),
				query.trim() || activeTag || null
			);
			setSuggestionSent(true);
			setTimeout(() => setSuggestion(""), 300);
		} catch (err) {
			setSuggestionError(
				err?.response?.data?.message ||
					err?.message ||
					"Error al enviar. Inténtalo de nuevo."
			);
		} finally {
			setSuggestionLoading(false);
		}
	};

	const handleQuickTag = (tag) => {
		if (activeTag === tag) {
			setActiveTag(null);
		} else {
			setActiveTag(tag);
		}
		setQuery("");
	};

	return (
		<Dialog
			open={open}
			onClose={onClose}
			fullWidth
			maxWidth="md"
			TransitionComponent={SlideUp}
			PaperProps={{
				sx: {
					borderRadius: "24px",
					maxHeight: "85vh",
					minHeight: 400,
					overflow: "hidden",
					boxShadow:
						"0 25px 60px rgba(0, 0, 0, 0.15), 0 0 0 1px rgba(0, 0, 0, 0.05)",
				},
			}}
			slotProps={{
				backdrop: {
					sx: {
						backgroundColor: "rgba(15, 23, 42, 0.6)",
						backdropFilter: "blur(8px)",
					},
				},
			}}
		>
			{/* ─── Search Header ─── */}
			<Box
				sx={{
					p: { xs: 1.5, sm: 2.5 },
					pb: 2,
					borderBottom: "1px solid rgba(0, 0, 0, 0.06)",
					background: "#fff",
				}}
			>
				<Box
					sx={{
						display: "flex",
						alignItems: "center",
						gap: 1.5,
						backgroundColor: "rgba(0, 0, 0, 0.03)",
						borderRadius: "16px",
						px: 2,
						py: 1.5,
						border: "2px solid #6366F1",
						boxShadow: "0 0 0 4px rgba(99, 102, 241, 0.1)",
					}}
				>
					<SearchIcon sx={{ color: "#6366F1", fontSize: 24 }} />
					<InputBase
						inputRef={inputRef}
						placeholder="Buscar destinos, guías, plantillas..."
						value={query}
						onChange={(e) => {
							setQuery(e.target.value);
							setActiveTag(null);
						}}
						sx={{
							flex: 1,
							fontSize: "1.05rem",
							fontWeight: 500,
							color: "#1a1a2e",
							"& input": { padding: 0 },
							"& input::placeholder": { color: "#8B8FA3", opacity: 1 },
						}}
					/>
					{query && (
						<IconButton
							size="small"
							onClick={() => {
								setQuery("");
								inputRef.current?.focus();
							}}
							sx={{ color: "#8B8FA3" }}
						>
							<CloseIcon sx={{ fontSize: 18 }} />
						</IconButton>
					)}
					<Box
						sx={{
							display: "flex",
							alignItems: "center",
							gap: 0.5,
							px: 1,
							py: 0.25,
							borderRadius: "8px",
							backgroundColor: "rgba(0, 0, 0, 0.06)",
							fontSize: "0.75rem",
							fontWeight: 500,
							color: "#8B8FA3",
							cursor: "pointer",
						}}
						onClick={onClose}
					>
						ESC
					</Box>
				</Box>

				{/* ─── Quick Tags ─── */}
				<Box sx={{ display: "flex", gap: 1, mt: 2, flexWrap: "wrap" }}>
					{QUICK_TAGS.map((tag) => (
						<Chip
							key={tag.label}
							label={tag.label}
							size="small"
							onClick={() => handleQuickTag(tag.label)}
							sx={{
								fontWeight: 600,
								fontSize: "0.813rem",
								borderRadius: "10px",
								height: 32,
								cursor: "pointer",
								backgroundColor:
									activeTag === tag.label
										? "rgba(99, 102, 241, 0.12)"
										: "rgba(0, 0, 0, 0.04)",
								color: activeTag === tag.label ? "#6366F1" : "#4B5563",
								border:
									activeTag === tag.label
										? "1px solid rgba(99, 102, 241, 0.3)"
										: "1px solid transparent",
								"&:hover": {
									backgroundColor:
										activeTag === tag.label
											? "rgba(99, 102, 241, 0.18)"
											: "rgba(0, 0, 0, 0.08)",
								},
								transition: "all 0.15s ease",
							}}
						/>
					))}
				</Box>
			</Box>

			{/* ─── Results Content ─── */}
			<Box
				sx={{
					flex: 1,
					overflowY: "auto",
					px: 2.5,
					py: 2,
					background: "#FAFBFC",
					"&::-webkit-scrollbar": { width: 6 },
					"&::-webkit-scrollbar-thumb": {
						backgroundColor: "rgba(0, 0, 0, 0.1)",
						borderRadius: 3,
					},
				}}
			>
				{/* ─── My Guides (si tiene y hay coincidencia) ─── */}
				{filteredMyGuides.length > 0 && (
					<Box sx={{ mb: 3 }}>
						<Box
							sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.5 }}
						>
							<FolderOutlinedIcon sx={{ fontSize: 18, color: "#8B8FA3" }} />
							<Typography
								sx={{
									fontSize: "0.75rem",
									fontWeight: 700,
									textTransform: "uppercase",
									letterSpacing: "0.05em",
									color: "#8B8FA3",
								}}
							>
								Mis guías
							</Typography>
						</Box>
						{filteredMyGuides.map((guide) => (
							<SearchResultItem
								key={`my-${guide.id}`}
								name={guide.name}
								subtitle="Guía personal"
								icon={
									<FolderOutlinedIcon sx={{ fontSize: 20, color: "#6366F1" }} />
								}
								onClick={() => {
									onClose();
									const userId = user?.uid || user?.id;
									if (userId) navigate(`/usuarios/${userId}/guias/${guide.id}`);
								}}
							/>
						))}
					</Box>
				)}

				{/* ─── Prebuilt guides results ─── */}
				{filteredPrebuilt.length > 0 && (
					<Box sx={{ mb: 3 }}>
						<Box
							sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.5 }}
						>
							<MapOutlinedIcon sx={{ fontSize: 18, color: "#8B8FA3" }} />
							<Typography
								sx={{
									fontSize: "0.75rem",
									fontWeight: 700,
									textTransform: "uppercase",
									letterSpacing: "0.05em",
									color: "#8B8FA3",
								}}
							>
								{isSearching ? "Resultados" : "Las más solicitadas"}
							</Typography>
						</Box>
						{filteredPrebuilt.map((guide) => (
							<SearchResultItem
								key={`pre-${guide.id}`}
								name={guide.name}
								subtitle={guide.description}
								days={guide.days}
								tags={guide.tags}
								coverImage={imageMap[guide.id] || guide.coverImage}
								onClick={() => handlePrebuiltClick(guide)}
							/>
						))}
					</Box>
				)}

				{/* ─── No results state ─── */}
				{isSearching && !hasResults && (
					<Fade in>
						<Box sx={{ textAlign: "center", py: 5 }}>
							<Box
								sx={{
									width: 72,
									height: 72,
									borderRadius: "50%",
									background:
										"linear-gradient(135deg, rgba(99, 102, 241, 0.1) 0%, rgba(168, 85, 247, 0.1) 100%)",
									display: "flex",
									alignItems: "center",
									justifyContent: "center",
									mx: "auto",
									mb: 2.5,
								}}
							>
								<LightbulbOutlinedIcon
									sx={{ fontSize: 32, color: "#6366F1" }}
								/>
							</Box>
							<Typography
								sx={{
									fontWeight: 700,
									fontSize: "1.125rem",
									color: "#1a1a2e",
									mb: 0.5,
								}}
							>
								No encontramos "{query || activeTag}"
							</Typography>
							<Typography
								sx={{
									color: "#8B8FA3",
									fontSize: "0.938rem",
									mb: 3,
									maxWidth: 380,
									mx: "auto",
									lineHeight: 1.5,
								}}
							>
								Pero nos encanta descubrir nuevos destinos. Propón la guía que
								te gustaría y la haremos realidad.
							</Typography>

							{/* Suggest form */}
							{!showSuggestForm && !suggestionSent && (
								<Button
									variant="contained"
									startIcon={<LightbulbOutlinedIcon />}
									onClick={() => setShowSuggestForm(true)}
									sx={{
										textTransform: "none",
										background:
											"linear-gradient(135deg, #6366F1 0%, #8B5CF6 100%)",
										fontWeight: 600,
										px: 3,
										py: 1.25,
										borderRadius: "14px",
										fontSize: "0.938rem",
										boxShadow: "0 4px 14px rgba(99, 102, 241, 0.35)",
										"&:hover": {
											background:
												"linear-gradient(135deg, #5558E3 0%, #7C4DE8 100%)",
											boxShadow: "0 6px 20px rgba(99, 102, 241, 0.45)",
											transform: "translateY(-1px)",
										},
										transition: "all 0.2s ease",
									}}
								>
									Proponer una guía
								</Button>
							)}

							{showSuggestForm && !suggestionSent && (
								<Fade in>
									<Box
										sx={{
											maxWidth: 420,
											mx: "auto",
											p: 2.5,
											borderRadius: "18px",
											backgroundColor: "#fff",
											border: "1px solid rgba(0, 0, 0, 0.06)",
											boxShadow: "0 4px 16px rgba(0, 0, 0, 0.06)",
										}}
									>
										<Typography
											sx={{
												fontWeight: 600,
												fontSize: "0.875rem",
												color: "#1a1a2e",
												mb: 1.5,
												textAlign: "left",
											}}
										>
											¿Qué destino o guía te gustaría?
										</Typography>
										<Box sx={{ display: "flex", gap: 1 }}>
											<InputBase
												placeholder="Ej: 5 días en Roma, Ruta por Japón..."
												value={suggestion}
												onChange={(e) => setSuggestion(e.target.value)}
												onKeyDown={(e) => {
													if (e.key === "Enter") handleSuggest();
												}}
												autoFocus
												sx={{
													flex: 1,
													px: 2,
													py: 1,
													borderRadius: "12px",
													backgroundColor: "rgba(0, 0, 0, 0.03)",
													border: "1px solid rgba(0, 0, 0, 0.08)",
													fontSize: "0.875rem",
													"&:focus-within": {
														borderColor: "#6366F1",
														boxShadow: "0 0 0 3px rgba(99, 102, 241, 0.1)",
													},
												}}
											/>
											<IconButton
												onClick={handleSuggest}
												disabled={!suggestion.trim() || suggestionLoading}
												sx={{
													background: suggestion.trim()
														? "linear-gradient(135deg, #6366F1 0%, #8B5CF6 100%)"
														: "rgba(0, 0, 0, 0.06)",
													color: suggestion.trim() ? "#fff" : "#8B8FA3",
													borderRadius: "12px",
													width: 44,
													height: 44,
													"&:hover": {
														background: suggestion.trim()
															? "linear-gradient(135deg, #5558E3 0%, #7C4DE8 100%)"
															: "rgba(0, 0, 0, 0.06)",
													},
												}}
											>
												{suggestionLoading ? (
													<CircularProgress size={18} sx={{ color: "#fff" }} />
												) : (
													<SendIcon sx={{ fontSize: 18 }} />
												)}
											</IconButton>
										</Box>
										{suggestionError && (
											<Typography
												sx={{
													fontSize: "0.813rem",
													color: "#EF4444",
													mt: 1,
													textAlign: "left",
												}}
											>
												{suggestionError}
											</Typography>
										)}
										<Typography
											sx={{
												fontSize: "0.75rem",
												color: "#8B8FA3",
												mt: 1.5,
												textAlign: "left",
											}}
										>
											Las más solicitadas se intentarán hacer primero ✨
										</Typography>
									</Box>
								</Fade>
							)}

							{suggestionSent && (
								<Fade in>
									<Box
										sx={{
											display: "flex",
											flexDirection: "column",
											alignItems: "center",
											gap: 1,
										}}
									>
										<Box
											sx={{
												width: 56,
												height: 56,
												borderRadius: "50%",
												background:
													"linear-gradient(135deg, rgba(16, 185, 129, 0.1) 0%, rgba(5, 150, 105, 0.1) 100%)",
												display: "flex",
												alignItems: "center",
												justifyContent: "center",
											}}
										>
											<CheckCircleOutlineIcon
												sx={{ fontSize: 28, color: "#10B981" }}
											/>
										</Box>
										<Typography
											sx={{
												fontWeight: 700,
												color: "#1a1a2e",
												fontSize: "1rem",
											}}
										>
											¡Propuesta enviada!
										</Typography>
										<Typography sx={{ color: "#8B8FA3", fontSize: "0.875rem" }}>
											Tendremos en cuenta tu sugerencia. ¡Gracias! 💜
										</Typography>
									</Box>
								</Fade>
							)}
						</Box>
					</Fade>
				)}

				{/* ─── Default state: Popular destinations when no search ─── */}
				{!isSearching &&
					filteredPrebuilt.length === 0 &&
					prebuiltGuides.length > 0 && (
						<Box sx={{ mb: 3 }}>
							<Box
								sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.5 }}
							>
								<TrendingUpIcon sx={{ fontSize: 18, color: "#8B8FA3" }} />
								<Typography
									sx={{
										fontSize: "0.75rem",
										fontWeight: 700,
										textTransform: "uppercase",
										letterSpacing: "0.05em",
										color: "#8B8FA3",
									}}
								>
									Las más solicitadas
								</Typography>
							</Box>
							{prebuiltGuides.slice(0, 6).map((guide) => (
								<SearchResultItem
									key={`pop-${guide.id}`}
									name={guide.name}
									subtitle={guide.description}
									days={guide.days}
									tags={guide.tags}
									coverImage={imageMap[guide.id] || guide.coverImage}
									onClick={() => handlePrebuiltClick(guide)}
								/>
							))}
						</Box>
					)}

				{/* ─── Suggest a guide (siempre visible al final cuando hay búsqueda) ─── */}
				{!isSearching && (
					<Box
						sx={{
							mt: 2,
							p: 2.5,
							borderRadius: "18px",
							background:
								"linear-gradient(135deg, rgba(99, 102, 241, 0.04) 0%, rgba(168, 85, 247, 0.04) 100%)",
							border: "1px dashed rgba(99, 102, 241, 0.2)",
						}}
					>
						<Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
							<Box
								sx={{
									width: 40,
									height: 40,
									borderRadius: "12px",
									background:
										"linear-gradient(135deg, rgba(99, 102, 241, 0.1) 0%, rgba(168, 85, 247, 0.1) 100%)",
									display: "flex",
									alignItems: "center",
									justifyContent: "center",
									flexShrink: 0,
								}}
							>
								<LightbulbOutlinedIcon
									sx={{ fontSize: 22, color: "#6366F1" }}
								/>
							</Box>
							<Box sx={{ flex: 1 }}>
								<Typography
									sx={{
										fontWeight: 600,
										fontSize: "0.875rem",
										color: "#1a1a2e",
									}}
								>
									¿No encuentras tu destino?
								</Typography>
								<Typography sx={{ fontSize: "0.813rem", color: "#8B8FA3" }}>
									Propón una guía y la intentaremos crear. Las más solicitadas
									se hacen primero.
								</Typography>
							</Box>
							<Button
								size="small"
								onClick={() => {
									setQuery("mi destino");
									setActiveTag("inexistente");
								}}
								sx={{
									textTransform: "none",
									color: "#6366F1",
									fontWeight: 600,
									fontSize: "0.813rem",
									borderRadius: "10px",
									"&:hover": { backgroundColor: "rgba(99, 102, 241, 0.08)" },
								}}
							>
								Proponer
							</Button>
						</Box>
					</Box>
				)}
			</Box>

			{/* ─── Footer ─── */}
			<Box
				sx={{
					px: { xs: 1.5, sm: 2.5 },
					py: 1.5,
					borderTop: "1px solid rgba(0, 0, 0, 0.06)",
					background: "#fff",
					display: "flex",
					alignItems: "center",
					justifyContent: "space-between",
					flexWrap: "wrap",
					gap: 1,
				}}
			>
				<Box
					sx={{
						display: { xs: "none", sm: "flex" },
						alignItems: "center",
						gap: 2,
					}}
				>
					<Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
						<Box
							sx={{
								px: 0.75,
								py: 0.25,
								borderRadius: "4px",
								backgroundColor: "rgba(0, 0, 0, 0.06)",
								fontSize: "0.688rem",
								fontWeight: 500,
								color: "#8B8FA3",
							}}
						>
							↑↓
						</Box>
						<Typography sx={{ fontSize: "0.75rem", color: "#8B8FA3" }}>
							Navegar
						</Typography>
					</Box>
					<Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
						<Box
							sx={{
								px: 0.75,
								py: 0.25,
								borderRadius: "4px",
								backgroundColor: "rgba(0, 0, 0, 0.06)",
								fontSize: "0.688rem",
								fontWeight: 500,
								color: "#8B8FA3",
							}}
						>
							↵
						</Box>
						<Typography sx={{ fontSize: "0.75rem", color: "#8B8FA3" }}>
							Abrir
						</Typography>
					</Box>
					<Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
						<Box
							sx={{
								px: 0.75,
								py: 0.25,
								borderRadius: "4px",
								backgroundColor: "rgba(0, 0, 0, 0.06)",
								fontSize: "0.688rem",
								fontWeight: 500,
								color: "#8B8FA3",
							}}
						>
							esc
						</Box>
						<Typography sx={{ fontSize: "0.75rem", color: "#8B8FA3" }}>
							Cerrar
						</Typography>
					</Box>
				</Box>
				<Typography
					sx={{
						fontSize: "0.75rem",
						color: "#8B8FA3",
						flexShrink: 0,
						ml: { xs: "auto", sm: 0 },
					}}
				>
					{filteredPrebuilt.length + filteredMyGuides.length} resultados
				</Typography>
			</Box>
		</Dialog>
	);
};

/* ─── Result Item subcomponent ─── */
const SearchResultItem = ({
	name,
	subtitle,
	days,
	tags,
	coverImage,
	icon,
	onClick,
}) => {
	return (
		<Box
			onClick={onClick}
			sx={{
				display: "flex",
				alignItems: "center",
				gap: { xs: 1.5, sm: 2 },
				px: { xs: 1.5, sm: 2 },
				py: 1.5,
				borderRadius: "14px",
				cursor: "pointer",
				transition: "all 0.15s ease",
				"&:hover": {
					backgroundColor: "#fff",
					boxShadow: "0 2px 12px rgba(0, 0, 0, 0.06)",
					transform: "translateX(4px)",
				},
				"&:hover .arrow-icon": {
					opacity: 1,
					transform: "translateX(0)",
				},
			}}
		>
			{/* Image or icon */}
			{coverImage ? (
				<Box
					sx={{
						width: { xs: 44, sm: 48 },
						height: { xs: 44, sm: 48 },
						borderRadius: "12px",
						overflow: "hidden",
						flexShrink: 0,
						border: "1px solid rgba(0, 0, 0, 0.06)",
					}}
				>
					<Box
						component="img"
						src={coverImage}
						alt={name}
						sx={{
							width: "100%",
							height: "100%",
							objectFit: "cover",
						}}
					/>
				</Box>
			) : (
				<Box
					sx={{
						width: { xs: 44, sm: 48 },
						height: { xs: 44, sm: 48 },
						borderRadius: "12px",
						backgroundColor: "rgba(99, 102, 241, 0.08)",
						display: "flex",
						alignItems: "center",
						justifyContent: "center",
						flexShrink: 0,
					}}
				>
					{icon || <MapOutlinedIcon sx={{ fontSize: 22, color: "#6366F1" }} />}
				</Box>
			)}

			{/* Info */}
			<Box sx={{ flex: 1, minWidth: 0 }}>
				<Typography
					sx={{
						fontWeight: 600,
						fontSize: "0.938rem",
						color: "#1a1a2e",
						lineHeight: 1.3,
						overflow: "hidden",
						textOverflow: "ellipsis",
						whiteSpace: "nowrap",
					}}
				>
					{name}
				</Typography>
				<Box
					sx={{
						display: "flex",
						alignItems: "center",
						gap: 1,
						mt: 0.25,
						minWidth: 0,
						flexWrap: "nowrap",
					}}
				>
					{days && (
						<Box
							sx={{
								display: "flex",
								alignItems: "center",
								gap: 0.25,
								flexShrink: 0,
								whiteSpace: "nowrap",
							}}
						>
							<AccessTimeIcon sx={{ fontSize: 13, color: "#8B8FA3" }} />
							<Typography sx={{ fontSize: "0.75rem", color: "#8B8FA3" }}>
								{days} días
							</Typography>
						</Box>
					)}
					{subtitle && (
						<Typography
							sx={{
								fontSize: "0.75rem",
								color: "#8B8FA3",
								overflow: "hidden",
								textOverflow: "ellipsis",
								whiteSpace: "nowrap",
								minWidth: 0,
								flex: "1 1 0",
							}}
						>
							{days ? "· " : ""}
							{subtitle}
						</Typography>
					)}
				</Box>
			</Box>

			{/* Tags */}
			{tags && tags.length > 0 && (
				<Box sx={{ display: { xs: "none", sm: "flex" }, gap: 0.5 }}>
					{tags.slice(0, 2).map((tag) => (
						<Chip
							key={tag}
							label={tag}
							size="small"
							sx={{
								height: 24,
								fontSize: "0.688rem",
								fontWeight: 500,
								backgroundColor: "rgba(0, 0, 0, 0.04)",
								color: "#64748B",
							}}
						/>
					))}
				</Box>
			)}

			{/* Arrow */}
			<ArrowForwardIcon
				className="arrow-icon"
				sx={{
					fontSize: 18,
					color: "#8B8FA3",
					opacity: 0,
					transform: "translateX(-8px)",
					transition: "all 0.15s ease",
				}}
			/>
		</Box>
	);
};

export default SearchOverlay;
